import { Children, Fragment } from 'react';
import { usePageTitle } from '@/ui/hooks/usePageTitle';
import { Inline } from '@/ui/layout/Inline';
import { PageHeader } from '@/ui/layout/PageHeader';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { PageShell } from '@/ui/primitives';
import { detailPageSectionGapToken } from './detailPageSpacing';
import type { SpacingToken } from '@/theme/tokens';
import type { ReactElement, ReactNode } from 'react';

type DetailPageTemplateProps = {
  title: ReactNode;
  pageTitle?: string;
  status?: ReactNode;
  headerMeta?: ReactNode;
  headerActions?: ReactNode;
  metadata?: ReactNode;
  summary?: ReactNode;
  primaryContent?: ReactNode;
  secondaryContent?: ReactNode;
  paddingTop?: SpacingToken;
  paddingBottom?: SpacingToken;
};

export const DetailPageTemplate = ({
  title,
  pageTitle,
  status,
  headerMeta,
  headerActions,
  metadata,
  summary,
  primaryContent,
  secondaryContent,
  paddingTop = '2xl',
  paddingBottom = '3xl',
}: DetailPageTemplateProps): ReactElement => {
  usePageTitle(pageTitle);
  const sections = Children.toArray(
    [metadata, summary, primaryContent, secondaryContent].filter(
      (section): section is ReactNode => section !== null && section !== undefined,
    ),
  );
  const [firstSection, ...remainingSections] = sections;
  const resolvedTitle =
    status !== undefined || headerMeta !== undefined ? (
      <Inline
        gap="md"
        align="baseline"
        wrap="wrap"
        sx={{ width: 'auto', display: 'inline-flex', maxWidth: '100%' }}
      >
        {title}
        {status !== undefined ? (
          <Inline
            gap="sm"
            align="center"
            wrap="nowrap"
            sx={{ width: 'auto', display: 'inline-flex', alignSelf: 'center' }}
          >
            {status}
          </Inline>
        ) : null}
        {headerMeta !== undefined ? (
          <Inline
            gap="none"
            align="center"
            wrap="nowrap"
            sx={{ width: 'auto', alignSelf: 'center' }}
          >
            {headerMeta}
          </Inline>
        ) : null}
      </Inline>
    ) : (
      title
    );

  return (
    <PageShell
      maxWidth="xl"
      paddingX="2xl"
      paddingY="xl"
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
    >
      <Stack gap={detailPageSectionGapToken}>
        <Stack gap={detailPageSectionGapToken}>
          <Section paddingY="xs">
            <PageHeader title={resolvedTitle} actions={headerActions} align="start" />
          </Section>
          {firstSection}
        </Stack>
        {remainingSections.map((section, index) => (
          <Fragment key={index}>{section}</Fragment>
        ))}
      </Stack>
    </PageShell>
  );
};
